import React, { useState, useEffect } from 'react';
import { supabaseAdmin } from '../../lib/supabase';
import './ModernLoginForm.css';

const ModernLoginForm = ({ onLogin }) => {
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [showQuickLogin, setShowQuickLogin] = useState(false); // Start with tenant selection
  const [showUserRoles, setShowUserRoles] = useState(false); // Second page
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingTenants, setLoadingTenants] = useState(true);

  // User role options for quick login
  const userRoles = [
    { 
      id: 'super-admin', 
      name: '超級管理員 (Super Admin)', 
      email: 'admin@myclass.edu.hk',
      icon: '👑',
      role: 'admin'
    },
    { 
      id: 'staff', 
      name: '學校職員 (School Staff)', 
      email: 'staff@myclass.edu.hk',
      icon: '🏫',
      role: 'staff'
    },
    { 
      id: 'teacher', 
      name: '教師 (Teacher)', 
      email: 'teacher@myclass.edu.hk',
      icon: '👨‍🏫',
      role: 'teacher'
    },
    { 
      id: 'student-a', 
      name: '學生A (Student A)', 
      email: 'studenta@myclass.edu.hk',
      icon: '👨‍🎓',
      role: 'student'
    },
    { 
      id: 'student-b', 
      name: '學生B (Student B)', 
      email: 'studentb@myclass.edu.hk',
      icon: '👨‍🎓',
      role: 'student'
    },
    { 
      id: 'student-c', 
      name: '學生C (Student C)', 
      email: 'studentc@myclass.edu.hk',
      icon: '👨‍🎓',
      role: 'student'
    },
    { 
      id: 'parent', 
      name: '家長 (Parent)', 
      email: 'parent@myclass.edu.hk',
      icon: '👨‍👩‍👧',
      role: 'parent'
    }
  ];

  // Fetch tenants from database
  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoadingTenants(true);
      
      const { data, error } = await supabaseAdmin
        .from('tenants')
        .select('id, name, code')
        .order('name');

      if (error) {
        console.error('Error fetching tenants:', error);
        // Use default tenants if fetch fails
        setTenants([
          { id: 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3', name: 'Tenant School I (MC01X68)', code: 'MC01X68' },
          { id: '1acd60af-8884-48bb-b1ea-13fbb935d3a8', name: 'Tenant School 2 (MC02X69)', code: 'MC02X69' },
          { id: 'adf83c8b-b623-4f69-b65f-25e11c2781ce', name: 'Tenant School 3 (System Default)', code: 'SYSTEM' },
          { id: null, name: 'No School (Default) 無學校 - Use default data - no tenant filter', code: 'DEFAULT' }
        ]);
      } else if (data && data.length > 0) {
        // Add "No School" option
        const tenantsWithDefault = [
          ...data,
          { id: null, name: 'No School (Default) 無學校 - Use default data - no tenant filter', code: 'DEFAULT' }
        ];
        setTenants(tenantsWithDefault);
      } else {
        // Use default tenants
        setTenants([
          { id: 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3', name: 'Tenant School I (MC01X68)', code: 'MC01X68' },
          { id: '1acd60af-8884-48bb-b1ea-13fbb935d3a8', name: 'Tenant School 2 (MC02X69)', code: 'MC02X69' },
          { id: 'adf83c8b-b623-4f69-b65f-25e11c2781ce', name: 'Tenant School 3 (System Default)', code: 'SYSTEM' },
          { id: null, name: 'No School (Default) 無學校 - Use default data - no tenant filter', code: 'DEFAULT' }
        ]);
      }

      // Set default selected tenant (first one)
      if (data && data.length > 0) {
        setSelectedTenant(data[0]);
      } else {
        setSelectedTenant({ id: 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3', name: 'Tenant School I (MC01X68)', code: 'MC01X68' });
      }
    } catch (err) {
      console.error('Error in fetchTenants:', err);
      // Use default tenants
      setTenants([
        { id: 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3', name: 'Tenant School I (MC01X68)', code: 'MC01X68' },
        { id: '1acd60af-8884-48bb-b1ea-13fbb935d3a8', name: 'Tenant School 2 (MC02X69)', code: 'MC02X69' },
        { id: 'adf83c8b-b623-4f69-b65f-25e11c2781ce', name: 'Tenant School 3 (System Default)', code: 'SYSTEM' },
        { id: null, name: 'No School (Default) 無學校 - Use default data - no tenant filter', code: 'DEFAULT' }
      ]);
      setSelectedTenant({ id: 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3', name: 'Tenant School I (MC01X68)', code: 'MC01X68' });
    } finally {
      setLoadingTenants(false);
    }
  };

  const handleContinueToLogin = () => {
    if (!selectedTenant) {
      setError('Please select a school first');
      return;
    }
    setShowUserRoles(true);
    setError('');
  };

  const handleBackToTenantSelection = () => {
    setShowUserRoles(false);
    setError('');
  };

  const handleQuickLogin = async (userRole) => {
    if (!selectedTenant) {
      setError('Please select a school first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let userData = {
        id: `demo_${userRole.id}`,
        email: userRole.email,
        username: userRole.id,
        role: userRole.role,
        tenant_id: selectedTenant.id,
        tenantId: selectedTenant.id,
        profile: {
          name: userRole.name.split('(')[0].trim(),
          english_name: userRole.name.match(/\((.*?)\)/)?.[1] || userRole.name,
          role: userRole.role
        }
      };

      // For teacher role, fetch real teacher data from database
      if (userRole.role === 'teacher' && selectedTenant.id) {
        try {
          const { data: teachers, error: teachersError } = await supabaseAdmin
            .from('saas_teachers')
            .select('*')
            .eq('tenant_id', selectedTenant.id)
            .limit(1);

          if (!teachersError && teachers && teachers.length > 0) {
            const teacher = teachers[0];
            userData = {
              id: teacher.teacher_id || teacher.id || `teacher_${Date.now()}`,
              email: teacher.email || userRole.email,
              username: teacher.teacher_id || teacher.teacher_no || userRole.id,
              role: 'teacher',
              tenant_id: selectedTenant.id,
              tenantId: selectedTenant.id,
              profile: {
                name: teacher.name || teacher.teacher_name || '老師',
                english_name: teacher.english_name || teacher.name || 'Teacher',
                role: 'teacher',
                teacher_id: teacher.teacher_id,
                teacher_no: teacher.teacher_no,
                subject: teacher.subject || teacher.subject_specialty,
                department: teacher.department,
                phone: teacher.phone
              }
            };
          }
        } catch (fetchError) {
          console.error('Error fetching teacher:', fetchError);
        }
      }

      const mockSession = {
        user: userData,
        access_token: 'demo_token',
        refresh_token: 'demo_refresh_token'
      };

      onLogin(mockSession);
    } catch (err) {
      setError('An error occurred during login.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!selectedTenant) {
      setError('Please select a school first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // This would be your actual authentication logic
      // For now, just mock the login
      const mockSession = {
        user: {
          id: 'email_user',
          email: email,
          username: email.split('@')[0],
          role: 'teacher', // Default role
          tenant_id: selectedTenant.id,
          tenantId: selectedTenant.id,
          profile: {
            name: email.split('@')[0],
            english_name: email.split('@')[0],
            role: 'teacher'
          }
        },
        access_token: 'email_token',
        refresh_token: 'email_refresh_token'
      };

      onLogin(mockSession);
    } catch (err) {
      setError('Invalid email or password');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loadingTenants) {
    return (
      <div className="modern-login-container">
        <div className="login-card">
          <div className="loading-tenants">
            <div className="spinner"></div>
            <p>Loading schools...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-login-container">
      <div className="login-card">
        {/* Header */}
        <div className="login-header">
          <div className="app-logo">
            <span className="logo-icon">📚</span>
            <span className="logo-text">ExamAI</span>
          </div>
        </div>

        {/* Welcome Section */}
        <div className="welcome-section">
          <h1>Welcome Back</h1>
          <p>Sign in to continue learning</p>
        </div>

        {/* Page 1: Tenant Selection */}
        {!showUserRoles && (
          <>
            {/* Quick Login Toggle */}
            {showQuickLogin && (
              <button 
                className="quick-login-toggle"
                onClick={() => setShowQuickLogin(false)}
              >
                🎯 Show quick login options
              </button>
            )}

            {/* Tenant Selection */}
            <div className="tenant-selection">
          <div className="tenant-header">
            <span className="tenant-icon">🏫</span>
            <span className="tenant-label">Tenant School (A2A)</span>
            <button className="hide-btn" onClick={() => {}}>Hide</button>
          </div>

          <div className="tenant-list">
            {tenants.map((tenant) => (
              <div
                key={tenant.id || 'default'}
                className={`tenant-item ${selectedTenant?.id === tenant.id ? 'selected' : ''}`}
                onClick={() => setSelectedTenant(tenant)}
              >
                <div className="tenant-info">
                  <span className="tenant-icon-small">
                    {tenant.id === null ? '🌐' : '🏫'}
                  </span>
                  <div className="tenant-details">
                    <div className="tenant-name">{tenant.name}</div>
                    {tenant.code && tenant.code !== 'DEFAULT' && (
                      <div className="tenant-code">{tenant.code} - A2A MyClassAI - 76 capabilities</div>
                    )}
                    {tenant.code === 'DEFAULT' && (
                      <div className="tenant-code">Use default data - no tenant filter</div>
                    )}
                  </div>
                </div>
                {selectedTenant?.id === tenant.id && (
                  <span className="selected-check">✓</span>
                )}
              </div>
            ))}
          </div>

          {selectedTenant && (
            <div className="selected-tenant-info">
              Selected: {selectedTenant.name}
            </div>
          )}
        </div>

            {/* Continue Button */}
            <button
              className="continue-btn"
              onClick={handleContinueToLogin}
              disabled={!selectedTenant || loading}
            >
              <span className="btn-icon">→</span>
              Continue to Login
            </button>

            {/* Alternative: Email Login Link */}
            {!showQuickLogin && (
              <button
                className="email-login-link"
                onClick={() => setShowQuickLogin(true)}
              >
                Or sign in with email address
              </button>
            )}
          </>
        )}

        {/* Page 2: User Role Selection */}
        {showUserRoles && (
          <>
            {/* Back Button */}
            <button
              className="back-to-tenant-btn"
              onClick={handleBackToTenantSelection}
            >
              ← Back to School Selection
            </button>

            {/* Selected Tenant Display */}
            <div className="current-tenant-display">
              <span className="tenant-icon-display">🏫</span>
              <div className="tenant-info-display">
                <div className="tenant-name-display">{selectedTenant.name}</div>
                <div className="tenant-code-display">{selectedTenant.code}</div>
              </div>
            </div>

            <div className="quick-login-section">
              <div className="section-header">
                <span className="section-icon">🎯</span>
                <span>Quick Login (Demo)</span>
              </div>
              
              <div className="user-roles-grid">
              {userRoles.map((role) => (
                <button
                  key={role.id}
                  className="role-button"
                  onClick={() => handleQuickLogin(role)}
                  disabled={loading || !selectedTenant}
                >
                  <span className="role-icon">{role.icon}</span>
                  <div className="role-info">
                    <div className="role-name">{role.name.split('(')[0].trim()}</div>
                    <div className="role-email">{role.email}</div>
                  </div>
                  <span className="role-arrow">→</span>
                </button>
              ))}
              </div>

              <div className="quick-login-note">
                Click any user to login instantly
              </div>
            </div>
          </>
        )}

        {/* Email Login Form (Alternative) */}
        {showQuickLogin && !showUserRoles && (
          <form onSubmit={handleEmailLogin} className="email-login-form">
            <div className="form-group">
              <label>Email Address</label>
              <div className="input-wrapper">
                <span className="input-icon">✉️</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teacher@myclass.edu.hk"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="input-wrapper">
                <span className="input-icon">🔒</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  required
                />
                <button type="button" className="toggle-password">
                  👁️
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
              <button type="button" className="forgot-password">
                Forgot password?
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="sign-in-btn"
              disabled={loading || !selectedTenant}
            >
              <span className="btn-icon">🔓</span>
              Sign In
            </button>

            <div className="form-footer">
              Don't have an account? <button type="button" className="sign-up-link">Sign up</button>
            </div>

            <button
              type="button"
              className="back-to-tenant-selection"
              onClick={() => setShowQuickLogin(false)}
            >
              ← Back to School Selection
            </button>
          </form>
        )}

        {/* Footer */}
        <div className="login-footer">
          © 2026 ExamAI. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default ModernLoginForm;
